import React from "react";
import WebToAppCampaign from "web/react/components/web-to-app-campaign/web-to-app-campaign";
import { useLeadLinkClick } from "web/react/hooks/use-lead-link-click/use-lead-link-click";
import { useProductClick } from "web/react/hooks/use-product-click/use-product-click";
import environment from "web/script/modules/environment";
import { languageIsEnglish } from "web/script/modules/language";
import storage from "web/script/utils/storage";
import { useProductCardContext } from "./product-card";

function getAnalyticsEventLabel(contextType, reason): string {
    const suffix = reason === "text" ? "_desc" : "";

    const analyticsEventLabels = {
        // maps analytics labels used in web to app campaigns to the context type
        "feed-product": `product_card_block${suffix}`,
        "related-product": `related_product_card${suffix}`,
    };

    return analyticsEventLabels[contextType];
}

interface ProductCardLinkProps {
    reason: string;
    predicate?: boolean;
    className?: string;
    children?: any;
    feedType?: string;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

function ProductCardLink({
    children,
    reason,
    className,
    predicate,
    onClick,
    onMouseEnter,
    onMouseLeave,
    feedType,
}: ProductCardLinkProps): React.ReactElement {
    const {
        id,
        uid,
        url,
        slug,
        is_affiliate: isAffiliate,
        lead_enabled: leadEnabled,
        image_url: imageURL,
        in_stock: inStock,
        link_id: linkId,
        lead_url: leadURL,
        affiliate_promo_url: affiliatePromoURL,
        context_type: contextType,
        overlay_enabled: overlayEnabled,
        excluded_from_app: retailerExcludedFromApp,
        app_deeplink_path: appDeeplinkPath,
        icon_enabled: checkoutEnabled,
        is_noindex_tagged: isNoIndexTagged,
        retailer_slug: retailerSlug,
        currency,
        hash_url: hashURL,
    } = useProductCardContext();

    const contextualisedReason = contextType ? `${contextType}-${reason}` : undefined;

    // MG-771: We want to disable leads on related products if the continue option has been clicked this session
    const stopLeadsOnReleatedProducts =
        storage.get("continueClicked", false, true) &&
        contextType === "related-product" &&
        environment.getFeature("mg_remove_leads_on_related_products_pdp");

    const isNewArrivalsExperiment =
        contextType === "new-product-pdp" &&
        environment.getFeature("mg_show_new_arrivals_rail_pdp");

    const shouldBlock =
        contextType === "related-product" &&
        storage.get("hasBeenBlockedProductCard", false, true) &&
        storage.get("continueClicked", false, true) &&
        languageIsEnglish();

    // Remove leads on Feeds for OOS PDPs
    const pageType = environment.get("pageType");
    const isFeedPage = pageType === "feed";
    const removeLeadsOnFeedsOOSItems = isFeedPage && !inStock;

    const shouldRenderLeadLink =
        !checkoutEnabled &&
        !shouldBlock &&
        isAffiliate &&
        leadEnabled &&
        !stopLeadsOnReleatedProducts &&
        !isNewArrivalsExperiment &&
        !removeLeadsOnFeedsOOSItems &&
        predicate;

    const { onClick: onProductClick } = useProductClick({
        id: id.toString(),
        productUrl: url,
        productImageUrl: imageURL || undefined,
        inStock,
        shouldRedirect: false,
        overlayEnabled: overlayEnabled,
        productUid: uid,
        productSlug: slug,
        reason: contextualisedReason,
        linkId: linkId?.toString(),
        href: url,
        isLeadLink: shouldRenderLeadLink,
        openInNewTab: shouldRenderLeadLink,
        contextType,
        feedType,
        appDeeplinkPath,
        checkoutEnabled,
        hashURL,
    });

    function beforeLeadLinkClick(callback, event): void {
        onProductClick(event);
        callback(event);
    }

    const { onClick: onLeadLinkClick, leadHref } = useLeadLinkClick({
        href: leadURL as string,
        productId: id.toString(),
        openInNewTab: true,
        affiliatePromoUrl: affiliatePromoURL || undefined,
        reason: contextualisedReason,
        beforeOnClick: beforeLeadLinkClick,
    });

    function handleClick(event): void {
        onProductClick(event);
    }

    function beforeCampaignOnClick(): void {
        // send analytics data before making a campaign decision
        onClick?.();
    }

    function onCampaignNotActive(event): void {
        if (shouldRenderLeadLink) {
            onLeadLinkClick(event);
        } else {
            handleClick(event);
        }
    }

    return (
        <WebToAppCampaign
            id={id.toString()}
            contextType={contextType || "product_card"}
            options={{
                isIcon: checkoutEnabled,
                action: "product_card_click",
                areaClicked: reason,
                hasBeenBlocked: storage.get("hasBeenBlockedOnce", false, true),
                newUserGenerated: storage.get("new_user_to_sign_up", null, true) === "complete",
                retailerExcludedFromApp: retailerExcludedFromApp,
                heimdallFlags: [
                    "eac_desktop_product_card_signup_block",
                    "eac_show_is_cheapest_possible_price_on_feed_modal",
                    "eac_open_pdp_on_sign_up_dismissal",
                    "eac_generic_feeds_product_card_signup",
                ],
                productId: id,
                linkId,
                currency,
                retailerSlug,
            }}
            beforeOnClick={beforeCampaignOnClick}
            defaultAction={onCampaignNotActive}
            analyticsEventLabel={getAnalyticsEventLabel(contextType || "", reason)}
            productImageUrl={imageURL || ""}
            continueOptionHref={url}
            appDeepLinkPath={appDeeplinkPath}
            productUrl={url}
        >
            {!shouldRenderLeadLink && (
                <a
                    rel={isNoIndexTagged ? "nofollow noreferrer" : undefined}
                    href={hashURL || url}
                    className={className}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    {children}
                </a>
            )}

            {shouldRenderLeadLink && (
                <a
                    href={leadHref}
                    className={className}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    target={"_blank"}
                    rel="nofollow noreferrer"
                    dangerouslySetInnerHTML={
                        typeof children === "string" ? { __html: children } : undefined
                    }
                >
                    {typeof children !== "string" ? children : null}
                </a>
            )}
        </WebToAppCampaign>
    );
}

export default ProductCardLink;
